import { AuditTrailAction } from '../../audit/domain/auditTrailAction';
import { DashboardComponentType } from '../../dashboards/domain/dashboardComponentType';
import { LogSorterColumnPlacement } from '../../logSorter/domain/logSorterBinLayout';
import { LogSorterNumberSequence } from '../../logSorter/domain/logSorterNumberSequence';
import { Anchor } from '../../map/domain/anchor';
import { ProductionOrderState } from '../../productionOrders/domain/productionOrderState';
import { ProductType } from '../../products/domain/productType';
import { ReportType } from '../../reports/domain/reportType';
import { ShapeMode } from '../../shapes/domain/shapeMode';
import { StorageAreaStatus } from '../../storageAreas/domain/storageAreaStatus';
import { StorageAreaType } from '../../storageAreas/domain/storageAreaType';
import { VehicleType } from '../../vehicles/domain/vehicleType';
import { VehicleSymbol } from '../../vehicles/domain/vehicleSymbol';
import { KeyboardActions } from '../../vehicles/domain/keyboardActions';
import { Unit } from '../../common/domain/bulkUnits';

export const english = {
  generic: {
    active: 'Active',
    language: 'Language',
    yes: 'Yes',
    no: 'No',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    id: 'Id',
    name: 'Name',
    description: 'Description',
    type: 'Type',
    code: 'Code',
    quality: 'Quality',
    columns: 'Columns',
    color: 'Color',
    capacity: 'Capacity',
    loadingTableData: 'Loading...',
    search: 'Search',
    generalErrorMsg: 'Ops! Something went wrong',
    signIn: 'Sign in',
    signOut: 'Sign out',
    width: 'Width',
    length: 'Length',
    height: 'Height',
    login: 'Login',
    logout: 'Logout',
    next: 'Next',
    back: 'Back',
    abort: 'Abort',
    close: 'Close',
    searchBy: 'Search by',
    clear: 'Clear',
    error: 'Error',
    success: 'Success',
    updated: 'Updated',
    added: 'Added',
    taskCompleted: 'Task completed',
    created: 'Created',
    deleted: 'Deleted',
    toggle: 'Toggle',
    status: 'Status',
    position: 'Position',
    direction: 'Direction',
    anchor: 'Anchor',
    product: 'Product',
    vehicle: 'Vehicle',
    map: 'Map',
    settings: 'Settings',
    geofence: 'Geofence',
    geofenceTop: 'Geofence top',
    geofenceBottom: 'Geofence bottom',
    geofenceLeft: 'Geofence left',
    geofenceRight: 'Geofence right',
    storageArea: 'Storage area',
    inventory: 'Inventory',
    noAvailableOptions: 'No available options',
    edit: 'Edit',
    select: 'Select',
    default: 'Default',
    mirrored: 'Mirrored',
    add: 'Add',
    top: 'Top',
    bottom: 'Bottom',
    left: 'Left',
    right: 'Right',
    longitude: 'Longitude',
    latitude: 'Latitude',
    shape: 'Shape',
    inside: 'Inside',
    outside: 'Outside',
    logSorter: 'Log sorter',
    confirm: 'Confirm',
    on: 'On',
    off: 'Off',
    circle: 'Circle',
    polygon: 'Polygon',
    size: 'Size',
    rotate: 'Rotate',
    freeform: 'Freeform',
    rectangle: 'Rectangle',
    empty: 'Empty',
    discard: 'Discard',
    audit: 'Audit',
    export: 'Export',
    import: 'Import',
    take: 'Take',
    ok: 'Ok',
    from: 'From',
    to: 'To',
    move: 'Move',
    coil: 'Coil',
    swap: 'Swap',
    copy: 'Copy',
    pick: 'Pick',
    unknown: 'Unknown',
    enabled: 'Enabled',
    note: 'Note',
    groups: 'Groups',
    productGroup: 'Product group',
    products: 'Products',
    numberOfLogs: 'Number of logs',
    balance: 'Balance',
    fifo: 'FIFO',
    volume: 'Volume',
    numberOfProductsInStorage: 'Number of products',
    storageAreas: 'Storage areas',
    showMore: 'Show more',
    preview: 'Preview',
    selectColumns: 'Select columns',
    locked: 'Locked',
    unlocked: 'Unlocked',
    keybindings: 'Keybindings',
    productionOrder: 'Production order',
    resource: 'Resource',
    priority: 'Priority',
    amount: 'Amount',
    newVersion: 'New version of SILog',
    important: 'Important',
    noData: 'No data',
    total: 'Total',
    calculate: 'Calculate',
    sorted: 'Sorted',
    unsorted: 'Unsorted',
    unit: 'Unit',
  },
  application: {
    exit: 'Exit',
    exitSilog: 'Exit SILog',
    confirmExit: 'Are you sure you want to exit SILog?',
    openSettings: 'Open app settings',
  },
  sites: {
    actions: {
      fetchMany: {
        title: 'Featching sites',
        error: 'Failed to fetch sites',
      },
    },
    notFound: 'Site not found',
  },
  sideMenu: {
    home: 'Home',
    map: 'Map',
    vehicles: 'Vehicles',
    products: 'Products',
    styles: 'Styles',
    coils: 'Coils',
    systemsettings: 'System settings',
    dashboards: 'Dashboards',
    productionOrders: 'Production orders',
    reports: 'Reports',
  },
  vehicleType(type: VehicleType): string {
    switch (type) {
      case VehicleType.Undefined:
        return 'Undefined';
      case VehicleType.Automatic:
        return 'Automatic';
      case VehicleType.LogHandler:
        return 'Log handler';
      case VehicleType.Truck:
        return 'Truck';
      case VehicleType.CoilForklift:
        return 'Coil forklift';
      case VehicleType.BulkHandler:
        return 'Bulk Handler';
    }
  },
  vehicleSymbol(symbol: VehicleSymbol): string {
    switch (symbol) {
      case VehicleSymbol.Undefined:
        return 'Arrow (Default)';
      case VehicleSymbol.LogStacker:
        return 'Log Stacker';
      case VehicleSymbol.WheelLoaderFrontGrapple:
        return 'Wheel Loader - Front Grapple';
      case VehicleSymbol.WheelLoaderHighGrapple:
        return 'Wheel Loader - High Grapple';
      case VehicleSymbol.TimberCrane:
        return 'Timber Crane';
      case VehicleSymbol.CoilForklift:
        return 'Coil Forklift';
    }
  },
  productType(type: ProductType): string {
    switch (type) {
      case ProductType.Log:
        return 'Log';
      case ProductType.Undefined:
        return 'Undefined';
      case ProductType.Coil:
        return 'Coil';
      case ProductType.Bulk:
        return 'Bulk';
    }
  },
  products: {
    actions: {
      create: {
        title: 'Creating product',
        error: 'Failed to create product',
      },
      update: {
        title: 'Updating product',
        error: 'Failed to update product',
      },
      delete: {
        title: 'Deleting product',
        error: 'Failed to delete product',
        statusCodes: {
          409: 'Product is still in use in storage area',
        },
      },
      fetch: {
        title: 'Fetching product',
        error: 'Failed to fetch product',
      },
      fetchMany: {
        title: 'Fetching products',
        error: 'Failed to fetch products',
      },
      editLogYardProducts(number: number) {
        return `Edit ${number} logyard products`;
      },
      editCoilProducts(number: number) {
        return `Edit ${number} coil products`;
      },
    },

    deleteDialog: {
      header(name: string) {
        return `Delete product ${name}`;
      },
      description: 'Are you sure you want to delete this product? This action cannot be undone.',
      deleteButton: 'Yes delete product',
    },

    products: 'Products',
    addNewProduct: 'Add new product',
    editProduct: 'Edit product',
    editMultipleProducts(number: number) {
      return `Edit ${number} products`;
    },
    fetchDataError: 'Failed to fetch product data',
    addNewProductError: 'Could not create product',
    editProductError: 'Could not update product',
    deleteProductError: 'Could not delete product:',
    noProductsFound: 'No products found.',
    loadingProductData: 'Loading product data..',
    logsPerSquareMeter: 'logs/m²',
    notFound: 'Product not found',
    couldNotFetchProduct: 'Could not fetch product!',
    logData: {
      logData: 'Log data',
      logsPerSquareMeter: 'Logs per m²',
      minDiameter: 'Min diameter',
      maxDiameter: 'Max diameter',
      maxLength: 'Max length',
      minLength: 'Min length',
      averageCylinderVolume: 'Average cylinder volume',
      averagePhysicalVolume: 'Average physical volume',
      isSorted: 'Is sorted',
      species: 'Species',
      logSorterName: 'Log sorter name',
      sawLineName: 'Sawline name',
      logsPerSquareMeterDescription:
        'Used to estimate how many logs a vehicle can take from a storage area',
    },
    coilData: {
      title: 'Coil',
      diameter: 'Diameter',
      depth: 'Depth',
    },
    bulkData: {
      title: 'Bulk',
      unit: 'Unit',
      density: 'Density',
    },
  },
  productGroups: {
    actions: {
      create: {
        title: 'Creating product group',
        error: 'Failed to create product group',
      },
      update: {
        title: 'Updating product group',
        error: 'Failed to update product group',
      },
      delete: {
        title: 'Deleting product group',
        error: 'Failed to delete product group',
      },
      fetch: {
        title: 'Fetching product group',
        error: 'Failed to fetch product group',
      },
      fetchMany: {
        title: 'Fetching product groups',
        error: 'Failed to fetch product groups',
      },
    },
    listPage: {
      title: 'Product groups',
      empty: 'No product groups',
      loading: 'Loading product groups...',
      addNew: 'Add new group',
      edit: 'Edit group',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete product group ${name}`;
      },
      description:
        'Are you sure you want to delete this product group? This action cannot be undone.',
      deleteButton: 'Yes delete product group',
    },
  },
  vehicles: {
    actions: {
      create: {
        title: 'Creating vehicle',
        error: 'Failed to create vehicle',
      },
      update: {
        title: 'Updating vehicle',
        error: 'Failed to update vehicle',
      },
      delete: {
        title: 'Deleting vehicle',
        error: 'Failed to delete vehicle',
      },
      fetch: {
        title: 'Fetching vehicle',
        error: 'Failed to fetch vehicle',
      },
      fetchMany: {
        title: 'Fetching vehicles',
        error: 'Failed to fetch vehicles',
      },
      startReservation: {
        title: 'Starting vehicle reservation',
        error: 'Failed to reserve vehicle',
      },
      endReservation: {
        title: 'Ending vehicle reservation',
        error: 'Failed to end vehicle reservation',
      },
      updateKeybinds: {
        title: 'Updating vehicle keybinds',
        error: 'Failed to vehicle keybinds',
      },
    },
    vehicles: 'Vehicles',
    noVehiclesFound: 'No vehicles found.',
    loadingVehicleData: 'Loading vehicle data..',
    addNewVehicle: 'Add new vehicle',
    editVehicle: 'Edit vehicle',
    confirmDeleteMsg: 'Are you sure you want to delete this vehicle? This action cannot be undone.',
    deleteBtnTxt: 'Yes delete vehicle',
    fetchDataError: 'Failed to fetch vehicle data',
    editVehicleSuccess: 'Edited',
    addNewVehicleSuccess: 'Success: added',
    deleteVehicleSuccess: 'Success: deleted',
    deleteVehicleError: 'Could not delete vehicle:',
    nameMustBeUnique: 'Name must be unique',
    symbol: 'Symbol',
    symbolPreview: 'Preview',
    capacityDescription:
      'Used to estimate how many logs that this vehicle can take from a storage area. It will be used in combination with the products "logs per square meter".',
    deleteVehicle(name: string) {
      return `Delete vehicle ${name}`;
    },
    inUseDialog: {
      header: 'Vehicle is in use!',
    },
    reservationEnded: 'Someone else took over your vehicle!',
    reservationEndedDescription: (userId: string) =>
      `Your vehicle was taken by ${userId.split('|')[0]}`,
    automaticLogYardMoves: 'Automatic log yard moves',
    automaticLogYardMovesLogSorterBinFull: 'When log sorter bin is full',
    automaticLogYardMovesLogSorterBinChangeProduct: 'When log sorter bin change product',
  },
  addLogSorterDialog: {
    addLogSorter: 'Add log sorter',
    editLogSorter: 'Edit log sorter',
    name: 'Name',
    bin: 'Bin',
    bins: 'Bins',
    identifiers: 'Id',
    logSorter: 'Log sorter',
    selectLogSorter: 'Select log sorter',
    startPositionX: 'Start position X',
    startPositionY: 'Start position Y',
    columns: 'Columns',
    numberOfBins: 'Number of bins',
    numberSequence: 'Number sequence',
    rotation: 'Rotation',
    direction: 'Direction',
    binWidth: 'Bin width',
    binLength: 'Bin height',
    distanceX: 'Distance between X',
    distanceY: 'Distance between Y',
    geoFenceOffsetTop: 'Geofence top',
    geoFenceOffsetBottom: 'Geofence bottom',
    geoFenceOffsetLeft: 'Geofence left',
    geoFenceOffsetRight: 'Geofence right',
    geoFenceOffsetInside: 'Geofence inside',
    geoFenceOffsetOutside: 'Geofence outside',
    addLogWhenSorted: 'Add log when sorted',
    intake: 'Intake',
    intakes: 'Intakes',
    addNewIntake: 'Add new intake',
    deleteIntake: 'Delete intake',
    savingStorageAreas: 'Saving storage areas',
    savingShapes: 'Saving shapes',
    savingLogSorter: 'Saving log sorter',
    success: 'Success!',
    savingBins: 'Saving bins',
    savingIntakes: 'Saving intakes',
    one: '1',
    twoLeft: '2L',
    twoRight: '2R',
  },
  dialogs: {
    storageArea: {
      addStorageArea: 'Create storage area',
      editStorageArea: 'Edit storage area',
      storageArea: 'Storage area',
      automaticProductChange: 'Automatic product change',
      allowMultipleProducts: 'Allow multiple products',
      allowedProducts: 'Allowed prodcuts',
      selectAllowedProducts: 'Select allowed products',
      selectStorageAreaType: 'Select storage area type',
      selectStorageAreaStatus: 'Select storage area status',
      confirmDeleteHeader: 'Delete storage area',
      confirmDeleteMessage: (name: string) =>
        `Are you sure you want to delete the storage area: ${name}?`,
      deleteError: 'Could not delete storage area',
      updateError: 'Could not update storage area',
      createError: 'Could not create storage area',
      editShape: 'Edit shape',
      sideArea: 'Side area',
      sideAreaDescription: 'Used to estimate the amount of logs that fits inside this storage area',
    },
    storageAreaSelect: {
      title: 'Storage area info',
    },
    inventory: {
      title: 'Inventory',
      selectProduct: 'Select product',
      currentAmount: 'Current amount:',
      setNewAmount: 'Set new amount',
      storageAreaNotFound: 'Storage area not found!',
      confirmHeader: (name: string) => `Inventory ${name}`,
      confirmMessage: (name: string, product: string) =>
        `Are you sure you want to add ${product} to ${name}? Allow multiple products is disabled.`,
    },
    confirmEmpty: {
      message:
        'There is still content in the storage area. Are you sure you wanna empty it? Remaining content will be deleted.',
      title: (name: string) => `Empty storage area ${name}`,
      willBeDeleted: 'will be deleted',
    },
    confirmAllowMultipleProducts: {
      message: (storageAreaName: string, product: string) =>
        `Allowed multiple products is disabled. Are you sure you wanna place "${product}" in ${storageAreaName}`,
      title: 'Warning',
    },
    confirmPlaceNotAllowedProductInLogYard: {
      message: (storageAreaName: string, products: string) =>
        `The product "${products}" are not allowed in ${storageAreaName}. Are you sure you want to place it here?`,
      title: 'Warning',
    },
    moveLogYardContent: {
      pickTab: 'Pick from',
      pickButton: 'Pick',
      placeTab: 'Place in',
      placeButton: 'Place',
      pickAll: 'Empty',
    },
    selectVehicle: {
      title: 'Select vehicle',
      message: 'Please select the vehicle you are using',
    },
    vehicleInventory: {
      vehicleInventory: 'Vehicle inventory',
      vehicleLoad: 'Current vehicle load',
      leaveTitle: 'Leave in',
      leaveButton: 'Leave',
      moving: 'Moving',
    },
    search: {
      searchForProduct: 'Search for a product',
      selectAProduct: 'Select a product',
    },
    mapView: {
      title: 'Add new map view',
      enterName: 'Enter a name',
    },
    selectLogSorter: {
      title: 'Select log sorter',
    },
    productionOrder: {
      editTitle: 'Select production order',
      selectProductionOrder: 'Select production order',
    },
  },
  error: {
    required: 'Required.',
    invalidValue: 'Invalid value.',
    nameAlreadyExist: 'Name already exist.',
    nameIsRequired: 'A name is required.',
    maxLength: (limit: number) => `Max length ${limit}`,
    forbidden: 'Forbidden',
    notFound: 'Not found',
    conflict: 'Conflict',
    network: 'Could not reach server',
    invalidDensity: 'Density needs to be atleast 0.1',
  },
  enums: {
    storageAreaStatus(status: StorageAreaStatus): string {
      switch (status) {
        case StorageAreaStatus.Empty:
          return 'Empty';
        case StorageAreaStatus.InUse:
          return 'In use';
        case StorageAreaStatus.Full:
          return 'Full';
        case StorageAreaStatus.Undefined:
          return 'Undefined';
      }
    },
    storageAreaType(type: StorageAreaType): string {
      switch (type) {
        case StorageAreaType.Producer:
          return 'Producer';
        case StorageAreaType.Consumer:
          return 'Consumer';
        case StorageAreaType.Vehicle:
          return 'Vehicle';
        case StorageAreaType.LogSorterIntake:
          return 'Logsorter intake';
        case StorageAreaType.SawLineIntake:
          return 'Sawline intake';
        case StorageAreaType.LogSorterBin:
          return 'Logsorter bin';
        case StorageAreaType.SortedPile:
          return 'Sorted pile';
        case StorageAreaType.UnsortedPile:
          return 'Unsorted pile';
        case StorageAreaType.Truck:
          return 'Truck';
        case StorageAreaType.Train:
          return 'Train';
        case StorageAreaType.Undefined:
          return 'Undefined';
        case StorageAreaType.CoilStack:
          return 'Coil stack';
        case StorageAreaType.Bulk:
          return 'Bulk';
      }
    },
    shapeMode(type: ShapeMode): string {
      switch (type) {
        case ShapeMode.Rectangle:
          return 'Rectangle';
        case ShapeMode.Circle:
          return 'Circle';
        case ShapeMode.Polygon:
          return 'Polygon';
      }
    },
    logSorterNumberSequence(value: LogSorterNumberSequence): string {
      switch (value) {
        case LogSorterNumberSequence.Sequential:
          return 'Sequential';
        case LogSorterNumberSequence.SequentialMirrored:
          return 'Sequential mirrored';
        case LogSorterNumberSequence.Alternating:
          return 'Alternating';
        case LogSorterNumberSequence.AlternatingMirrored:
          return 'Alternating mirrored';
      }
    },
    logSorterBinLayout(value: LogSorterColumnPlacement): string {
      switch (value) {
        case LogSorterColumnPlacement.Left:
          return 'Left';
        case LogSorterColumnPlacement.Right:
          return 'Right';
      }
    },
    anchor(value: Anchor): string {
      switch (value) {
        case Anchor.Center:
          return 'Center';
        case Anchor.BottomCenter:
          return 'Bottom Center';
        case Anchor.BottomLeft:
          return 'Bottom Left';
        case Anchor.BottomRight:
          return 'Bottom Right';
        case Anchor.TopCenter:
          return 'Top Center';
        case Anchor.TopLeft:
          return 'Top Left';
        case Anchor.TopRight:
          return 'Top Right';
      }
    },
    auditTrailAction(action: AuditTrailAction): string {
      switch (action) {
        case AuditTrailAction.InventoryApiExecute:
          return 'Inventory';
        case AuditTrailAction.InventoryApiUndo:
          return 'Undo inventory';
        case AuditTrailAction.LogSorterApiCreate:
          return 'Create log sorter';
        case AuditTrailAction.LogSorterApiDelete:
          return 'Delete log sorter';
        case AuditTrailAction.LogSorterApiUpdate:
          return 'Update log sorter';
        case AuditTrailAction.MapStyleApiCreate:
          return 'Create map style';
        case AuditTrailAction.MapStyleApiDelete:
          return 'Delete map style';
        case AuditTrailAction.MapStyleApiUpdate:
          return 'Update map style';
        case AuditTrailAction.MoveApiUndo:
          return 'Undo move';
        case AuditTrailAction.MoveApi:
          return 'Move';
        case AuditTrailAction.ProductApiCreate:
          return 'Create product';
        case AuditTrailAction.ProductApiDelete:
          return 'Delete product';
        case AuditTrailAction.ProductApiUpdate:
          return 'Update product';
        case AuditTrailAction.ShapeApiCreate:
          return 'Create shape';
        case AuditTrailAction.ShapeApiDelete:
          return 'Delete shape';
        case AuditTrailAction.ShapeApiUpdate:
          return 'Update shape';
        case AuditTrailAction.ShapeApiUndoDelete:
          return 'Undo update shape';
        case AuditTrailAction.StorageAreaApiCreate:
          return 'Create storage area';
        case AuditTrailAction.StorageAreaApiDelete:
          return 'Delete storage area';
        case AuditTrailAction.StorageAreaApiUndoDelete:
          return 'Undo delete storage area';
        case AuditTrailAction.StorageAreaApiUpdate:
          return 'Update storage area';
        case AuditTrailAction.SyncMove:
          return 'Move';
        case AuditTrailAction.VehicleApiCreate:
          return 'Create vehicle';
        case AuditTrailAction.VehicleApiDelete:
          return 'Delete vehicle';
        case AuditTrailAction.VehicleApiUpdate:
          return 'Update vehicle';
        case AuditTrailAction.Undefined:
          return 'Undefined';
        case AuditTrailAction.SyncInventory:
          return 'Sync inventory';
        case AuditTrailAction.SyncProduct:
          return 'Sync product';
        case AuditTrailAction.SyncShape:
          return 'Sync shape';
        case AuditTrailAction.SyncStorageArea:
          return 'Sync storage area';
        case AuditTrailAction.SyncUndoInventory:
          return 'Sync undo inventory';
        case AuditTrailAction.SyncUndoMove:
          return 'Sync undo move';
        case AuditTrailAction.SyncUndoStorageArea:
          return 'Synt undo storage area';
        case AuditTrailAction.CoilApiCreate:
          return 'Create coil';
        case AuditTrailAction.CoilApiUpdate:
          return 'Update coil';
        case AuditTrailAction.CoilApiDelete:
          return 'Delete coil';
        case AuditTrailAction.FeatureFlagApi:
          return 'Update Feature flags';
        case AuditTrailAction.SettingUpdate:
          return 'Update settings';
        case AuditTrailAction.SyncFeatureFlag:
          return 'Sync feature flags';
        case AuditTrailAction.SyncLogSorter:
          return 'Sync log sorter';
        case AuditTrailAction.SyncMapStyle:
          return 'Sync map styles';
        case AuditTrailAction.SyncSetting:
          return 'Sync settings';
        case AuditTrailAction.SyncVehicle:
          return 'Sync vehicles';
        case AuditTrailAction.ProductGroupApiCreate:
          return 'Create product group';
        case AuditTrailAction.ProductGroupApiUpdate:
          return 'Update product group';
        case AuditTrailAction.ProductGroupApiDelete:
          return 'Delete product group';
        case AuditTrailAction.ProductGroupSync:
          return 'Sync product group';
        case AuditTrailAction.DashboardCreate:
          return 'Create dashboard';
        case AuditTrailAction.DashboardUpdate:
          return 'Update dashboard';
        case AuditTrailAction.DashboardDelete:
          return 'Delete dashboard';
        case AuditTrailAction.MoveTimberFromLogSorterBins:
          return 'Move timber from log sorter bins';
        case AuditTrailAction.ProductionOrderCreate:
          return 'Create production order';
        case AuditTrailAction.ProductionOrderUpdate:
          return 'Update production order';
        case AuditTrailAction.ProductionOrderDelete:
          return 'Delete production order';
        case AuditTrailAction.ProductionOrderSync:
          return 'Sync production order';
        case AuditTrailAction.ProductionOrderChange:
          return 'Change production order';
      }
    },
    dashboardComponentType(type: DashboardComponentType): string {
      switch (type) {
        case DashboardComponentType.StorageAreaTable:
          return 'Storage area table';
        case DashboardComponentType.LatestReleaseNotes:
          return 'Latest release notes';
        case DashboardComponentType.ProductGroupDiagram:
          return 'Product group diagram';
        case DashboardComponentType.ProductGroupTable:
          return 'Product group table';
        case DashboardComponentType.ProductTable:
          return 'Product table';
        case DashboardComponentType.AsljungapallenLagersaldo:
          return 'Asljungapallen in stock';
        default:
          return 'Unknown dashboardcomponenttype';
      }
    },
    keyboardAction(action: KeyboardActions): string {
      switch (action) {
        case KeyboardActions.LeaveInLogYard:
          return 'Leave';
        case KeyboardActions.PickFromLogYard:
          return 'Pick up';
      }
    },
    productionOrderState(state: ProductionOrderState): string {
      switch (state) {
        case ProductionOrderState.Deleted:
          return 'Deleted';
        case ProductionOrderState.Planned:
          return 'Planned';
        case ProductionOrderState.Released:
          return 'Released';
        case ProductionOrderState.Reserved:
          return 'Reserved';
        case ProductionOrderState.Preparing:
          return 'Preparing';
        case ProductionOrderState.Prepared:
          return 'Prepared';
        case ProductionOrderState.Delivering:
          return 'Delivering';
        case ProductionOrderState.Completed:
          return 'Completed';
        case ProductionOrderState.Registered:
          return 'Registered';
        case ProductionOrderState.PreparingPaused:
          return 'Preparing paused';
        case ProductionOrderState.DeliveringPaused:
          return 'Delivering paused';
      }

      return 'Unknown';
    },
  },
  reports: {
    exportFormat: {
      pdf: 'PDF',
      excel: 'Excel',
    },
    reportName(report: ReportType): string {
      switch (report) {
        case ReportType.LogYardStatus:
          return 'Status report';
        case ReportType.LogYardPeriod:
          return 'Period report';
        default:
          return 'Unknown report';
      }
    },
  },
  storageAreas: {
    actions: {
      create: {
        title: 'Create storage area',
        pending: 'Creating storage area',
        error: 'Failed to create storage area',
      },
      fetch: {
        title: 'Fetching storage area',
        error: 'Failed to fetch storage area',
      },
      fetchMany: {
        title: 'Fetching storage areas',
        error: 'Failed to fetch storage areas',
      },
      inventory: {
        title: 'Inventory storage area',
        error: 'Failed to inventory storage area',
      },
      undoInventory: {
        title: 'Undo inventory',
        error: 'Failed to undo inventory',
      },
      delete: {
        title: 'Deleting storage area',
        error: 'Failed to delete storage area',
      },
      update: {
        title: 'Updating storage area',
        error: 'Failed to update storage area',
      },
      undoDelete: {
        title: 'Undo delete storage area',
        error: 'Failed to undo delete storage area',
      },
    },
    copy: 'Copy',
    notFound: 'Storage area not found',
    storageAreaTable: {
      firstMoveTo: 'First move',
      daysSinceFirstMove: 'Days since first move',
    },
  },
  productionOrders: {
    actions: {
      fetchMany: {
        title: 'Fetching production orders',
        error: 'Failed to fetch production orders',
      },
      fetch: {
        title: 'Fetching production order',
        error: 'Failed to fetch production order',
      },
      create: {
        title: 'Creating production order',
        error: 'Failed to create production order',
      },
      update: {
        title: 'Updating production order',
        error: 'Failed to update production order',
      },
      delete: {
        title: 'Deleting production order',
        error: 'Failed to delete production order',
        statusCodes: {
          409: 'Failed to delete production order',
        },
      },
      change: {
        title: 'Changing production order',
        error: 'Failed to change production order',
      },
    },
    listPage: {
      title: 'Production orders',
      noFound: 'No production order found',
      loading: 'Loading production orders...',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete production order ${name}`;
      },
      description:
        'Are you sure you want to delete this production order? This action cannot be undone.',
      deleteButton: 'Yes delete production order',
    },
    editPage: {
      editTitle: 'Edit production order',
      createTitle: 'Create production order',
      productionInstruction: 'Production instruction',
      vehicleInstruction: 'Vehicle instruction',
      state: 'State',
      recipeInstruction: 'Instructions',
      recipeMachineInstruction: 'Machine instructions',
      consumeEntireStock: 'Consume entire stock',
      noProducts: 'No products',
      inProductsTitle: 'In products',
      outProductsTitle: 'Out products',
      editRecipeTitle: 'Edit recipe',
      addRecipeTitle: 'Add recipe',
      addRecipeButton: 'Add recipe',
      recipesTitle: 'Recipes',
    },
    plannedStart: 'Planned start',
  },
  shapes: {
    name: (id: string) => `Shape #${id}`,
    multipleNames: (number: number) => `Edit (${number}) shapes`,
    actions: {
      fetchMany: {
        title: 'Fetching shapes',
        error: 'Failed to fetch shapes',
      },
      fetch: {
        title: 'Fetching shape',
        error: 'Failed to fetch shape',
      },
      update: {
        title: 'Updating shape',
        error: 'Failed to update shape',
      },
      create: {
        title: 'Create shape',
        pending: 'Creating shape',
        error: 'Failed to create shape',
      },
      delete: {
        title: 'Deleting shape',
        error: 'Failed to delete shape',
      },
      query: {
        title: 'Querying shapes',
        error: 'Failed to query shapes',
      },
      undoDelete: {
        title: 'Undo delete shape',
        error: 'Failed to undo delete shape',
      },
    },
    geofenceFieldSet: 'Geofence',
  },
  settings: {
    actions: {
      read: {
        title: 'Read settings',
        error: 'Failed to read settings',
      },
      save: {
        title: 'Save settings',
        error: 'Failed to save settings',
      },
    },
    general: {
      title: 'General',
      language: 'Language',
      dark: 'Dark',
      light: 'Light',
      colorMode: 'Color mode',
      scale: 'Size of user interface',
    },
    map: {
      title: 'Map',
      style: 'Style',
      coordinate: 'Coordinates',
      centerMapOnVehicle: 'Center map on vehicle',
    },
    keybinds: {
      title: 'Keybinds',
    },
    about: {
      title: 'About',
    },
    vehicle: {
      title: 'Vehicle',
      activeVehicle: 'Active vehicle',
      speedLimit: 'Speed limit 5 km/h to trigger storage area',
    },
  },
  featureFlags: {
    actions: {
      read: {
        title: 'Read feature flags',
        error: 'Failed to read feature flags',
      },
      refresh: {
        title: 'Refresh feature flags',
        error: 'Failed to refresh feature flags',
      },
      toggle: {
        title: 'Toggle feature flags',
        error: 'Failed to toggle feature flags',
      },
    },
    refresh: {
      success: 'Feature flags refreshed successfully',
    },
    toggle: {
      success: 'Feature flag toggled successfully',
    },
    component: {
      title: 'Feature flags',
      description: 'Enable or disable features in the application',
      refreshButton: 'Refresh feature flags',
    },
  },
  auth: {
    loggedOut: {
      title: 'You are logged out!',
      description: 'You need to login to be able to continue working.',
    },
    unauthorized: {
      title: 'Your user is not allowed to use this app',
      description: 'You need to talk to your administrator to give you the right to use it.',
    },
  },
  map: {
    contextmenu: {
      create: {
        title: 'Create',
        shape: 'Shape',
        storageArea: 'Storage area',
        logSorter: 'Log sorter',
        intake: 'Intake',
      },
      edit: 'Edit',
      delete: 'Delete',
      audit: 'Audit',
      copy: 'Copy',
      move: 'Move',
    },
    actions: {
      saveMapMode: {
        title: 'Save map mode',
        error: 'Failed to save map mode',
      },
    },
  },
  logSorter: {
    actions: {
      query: {
        title: 'Querying log sorter',
        error: 'Failed to query log sorter',
      },
      update: {
        title: 'Updating log sorter',
        error: 'Failed to update log sorter',
      },
      create: {
        title: 'Creating log sorter',
        error: 'Failed to create log sorter',
      },
      delete: {
        title: 'Deleting log sorter',
        error: 'Failed to delete log sorter',
      },
      fetch: {
        title: 'Fetching log sorter',
        error: 'Failed to fetch log sorter',
      },
    },
    drawer: {
      createTitle: 'Create log sorter',
      binName: (binNumber: number) => `Bin ${binNumber}`,
      logSorterTab: 'Log sorter',
      binsTab: 'Bins',
    },
    deleteConfirm: {
      title: `Delete log sorter`,
      message: (name: string) => `Are you sure you want to delete the log sorter ${name}?`,
    },
    panel: {
      identifier: 'Identifier',
      numberSequence: 'Number sequence',
      numberOfBinsCol1: 'Number of bins in column one',
      numberOfBinsCol2: 'Number of bins in column two',
      binLayout: 'Column two to',
      binWidth: 'Bin width',
      binLength: 'Bin length',
      columnGap: 'Column gap',
      rowGap: 'Row gap',
      intakes: 'Intakes',
      offsetY: 'Offset Y',
      offsetX: 'Offset X',
    },
  },
  mapStyles: {
    actions: {
      create: {
        title: 'Creating style',
        error: 'Failed to create style',
      },
      update: {
        title: 'Updating style',
        error: 'Failed to update style',
      },
      delete: {
        title: 'Deleting style',
        error: 'Failed to delete style',
      },
      fetch: {
        title: 'Fetching style',
        error: 'Failed to fetch style',
      },
      fetchMany: {
        title: 'Fetching styles',
        error: 'Failed to fetch styles',
      },
    },

    deleteDialog: {
      header(name: string) {
        return `Delete style ${name}`;
      },
      description: 'Are you sure you want to delete this style? This action cannot be undone.',
      deleteButton: 'Yes delete style',
    },

    editDialog: {
      create: {
        title: 'Create new style',
      },
      edit: {
        title: 'Edit style',
      },
      tip: 'You can press ctrl+space to open the show info about avaliable functions',
      documentation: 'For more information about the formulas, see the ',
      documentationLinkText: 'map style documentation',
      shapeSet: 'Shape',
      vehicleSet: 'Vehicle',
      labelFormula: 'Label',
      colorFormula: 'Color',
      outlineColorFormula: 'Outline color',
      outlineSizeFormula: 'Outline size',
      fontSizeFormula: 'Font size',
      messageIconFormula: 'Show message icon',
      colorPicker: 'Color Picker (Used to display and select color codes)',
    },

    page: {
      title: 'Map Styles',
      notFound: 'No map styles found',
      loading: 'Loading map styles',
      copiedToClipboard: 'Copied to clipboard!',
      noStyleInClipboard: 'No style in clipboard',
      noStyleInClipboardDescription:
        'You need to export a style into clipboard before you can import it.',
      importFromClipboardTooltip: 'Import from clipboard',
    },
  },
  systemSettings: {
    page: {
      title: 'System Settings',
      settingsSaved: 'Settings saved successfully',
      storagearea: {
        title: 'Storage area',
        nameFormat: 'Name format',
        nameFormatRequired: 'Name format is required',
        nameFormatMissingSequence: 'Name format must contain placeholder {#} for sequence',
        logYardLength: 'Log yard length',
        logYardWidth: 'Log yard width',
        logYardSideArea: 'Log yard side area',
        logYard: 'Log yard',
      },
      featureflags: {
        title: 'Feature Flags',
      },
      reports: {
        title: 'Reports',
      },
      map: {
        minZoom: 'The minimum zoom level of the map (0-24)',
      },
      dashboards: {
        title: 'Dashboardcomponents',
      },
    },
  },
  actions: {
    fetchVersion: {
      title: 'Fetching application version',
      error: 'Failed to fetch application version',
    },
  },
  undo: {
    undoBtn: 'Undo',
    pickTitle: 'Undo pick action',
    getUndoPickDescription(
      oldStorageAreaCount: number,
      oldVehicleCount: number,
      storageAreaName: string,
    ): string {
      return `Confirm to remove ${oldVehicleCount} logs from your vehicle and restore storage area ${storageAreaName} to amount: ${oldStorageAreaCount}`;
    },
    placeTitle: 'Undo place action',
    getUndoPlaceDescription(numberOfLogs: number, storageAreaName: string): string {
      return `Confirm to restore ${numberOfLogs} logs to your vehicle and remove ${numberOfLogs} logs from storage area ${storageAreaName}`;
    },
    inventoryTitle: 'Undo inventory action',
    getUndoInventoryDescription(
      productname: string,
      numberOfLogs: number,
      storageAreaName: string,
    ): string {
      return `Confirm to undo the latest inventory action on storageArea ${storageAreaName} and set amount of ${productname} back to ${numberOfLogs}`;
    },
    getUndoBulkInventoryDescription(
      productname: string,
      value: number,
      unit: Unit,
      storageAreaName: string,
    ): string {
      return `Confirm to undo the latest inventory action on storage area ${storageAreaName} and set amount of ${productname} back to ${value}`;
    },
    createStorageAreaTitle: 'Undo create storage area',
    getUndoCreateStorageAreaDescription(storageAreaName: string): string {
      return `Confirm to undo create storage area ${storageAreaName}`;
    },
    deleteStorageAreaTitle: 'Undo delete storage area',
    getUndoDeleteStorageAreaDescription(storageAreaName: string): string {
      return `Confirm to undo delete storage area ${storageAreaName}`;
    },
    editStorageAreaTitle: 'Undo edit storage area',
    getUndoEditStorageAreaDescription(storageAreaName: string): string {
      return `Confirm to undo edit on storage area ${storageAreaName}`;
    },
    createShape: {
      title: 'Undo create shape',
      description: `Confirm to undo create shape`,
    },
    updateShape: {
      title: 'Undo update shape',
      description: 'Confirm to undo update shape',
    },
    deleteShape: {
      title: 'Undo delete shape',
      description: 'Confirm to undo delete shape',
    },
  },
  quickMenu: {
    addStorageArea: 'Create storage area',
    editStorageArea: 'Edit storage area',
  },
  moves: {
    actions: {
      moveTimberFromLogSorterBins: {
        title: 'Move timber from log sorter bins',
        error: 'Failed to move timber from log sorter bins',
      },
      pick: {
        title: 'Picking from storage area',
        error: 'Failed to pick from storage area',
      },
      place: {
        title: 'Place in storage area',
        error: 'Failed to place from storage area',
      },
      pickAndPlace: {
        title: 'Pick and place',
        error: 'Failed to pick and place',
      },
    },
    failedDialog: {
      title: 'Move failed!',
      description: (fromStorageArea: string, toStorageArea: string) =>
        `Move from ${fromStorageArea} to ${toStorageArea}`,
    },
    moveTimberFromLogSorterBins: 'Move timber from bins',
  },
  home: {
    welcome: 'Welcome to',
    description: 'Next generation logistical system',
    username: 'Username',
    enterUsername: 'Enter your username to sign in',
    rememberUsername: 'Remember username',
    signInButton: 'Sign in',
  },
  unsavedChanges: {
    title: 'You have unsaved changes!',
    description: 'What do you want to do with them?',
  },
  audit: {
    dialog: {
      title: (name: string) => `Changes of ${name}`,
    },
    actions: {
      fetchShape: {
        title: 'Fetching shape audit',
        error: 'Failed to fetch shape audit',
      },
      fetchVehicle: {
        title: 'Fetching vehicle audit',
        error: 'Failed to fetch vehicle audit',
      },
      fetchProduct: {
        title: 'Fetching product audit',
        error: 'Failed to fetch product audit',
      },
      fetchProductGroup: {
        title: 'Fetching product group audit',
        error: 'Failed to fetch product group audit',
      },
      fetchDashboard: {
        title: 'Fetching dashboard audit',
        error: 'Failed to fetch dashboard audit',
      },
      fetchProductionOrder: {
        title: 'Fetching production order audit',
        error: 'Failed to fetch production order audit',
      },
    },
  },
  units: {
    millimeters: 'mm',
    cubicMeter: 'm³',
    kilogram: 'kg',
    ton: 'ton',
    kgPerM3: 'kg/m3',
  },
  coils: {
    actions: {
      fetchMany: {
        title: 'Fetching coils',
        error: 'Failed to fetch coils',
      },
      fetch: {
        title: 'Fetching coil',
        error: 'Failed to fetch coil',
      },
      create: {
        title: 'Creating coil',
        error: 'Failed to create coil',
      },
      update: {
        title: 'Updating coil',
        error: 'Failed to update coil',
      },
      delete: {
        title: 'Deleting coil',
        error: 'Failed to delete coil',
      },
      undoMove: {
        title: 'Undo coil move',
        error: 'Failed to undo coil move',
      },
    },

    page: {
      title: 'Coils',
      notFound: 'No coils found',
      loading: 'Loading coils',
    },

    deleteDialog: {
      header(name: string) {
        return `Delete coil ${name}`;
      },
      description: 'Are you sure you want to delete this coil? This action cannot be undone.',
      deleteButton: 'Yes delete coil',
    },

    editDialog: {
      create: {
        title: 'Create new coil',
      },
      edit: {
        title: 'Edit coil',
      },
    },

    slots: 'Number of slots',
    slotWidth: 'Slot width',
    maxHeight: 'Max height',
    maxLevels: 'Max levels',
    diameter: 'Diameter',
    depth: 'Depth',
    product: 'Product',
    storageArea: 'Storage area',
    position: 'Position',
    level: 'Level',
    maxCoilDiameter: 'Max coil diameter',

    moveDialog: {
      title: 'Move coil',
    },

    unableToDrawCoils: (coils: string[]) => {
      if (coils.length === 1) {
        return `${coils[0]} is on a invalid position inside the storage area`;
      } else {
        return 'Many coils have invalid positions inside the storage area';
      }
    },
  },
  logYard: {
    moveDialog: {
      title: 'Move logs',
    },
  },
  bulk: {
    actions: {
      fetch: {
        title: 'Fetching bulk',
        error: 'Failed to fetch bulk',
      },
      create: {
        title: 'Creating bulk',
        error: 'Failed to create bulk',
      },
      update: {
        title: 'Updating bulk',
        error: 'Failed to update bulk',
      },
      delete: {
        title: 'Deleting bulk',
        error: 'Failed to delete bulk',
      },
      undoMove: {
        title: 'Undo bulk move',
        error: 'Failed to undo bulk move',
      },
    },
    maxWeight: 'Max weight',
    maxVolume: 'Max volume',

    bulkUnit(value?: Unit | null): string {
      switch (value) {
        case Unit.Kilogram:
          return 'kilogram';
        case Unit.CubicMeter:
          return 'cubic meters';
        case Unit.LongTon:
          return 'long tons';
        case Unit.MetricTon:
          return 'metric tons';
        case Unit.Pound:
          return 'pounds';
        case Unit.ShortTon:
          return 'short tons';
        default:
          return '';
      }
    },
    bulkUnitShort(value?: Unit | null): string {
      switch (value) {
        case Unit.MetricTon:
          return 't';
        case Unit.Kilogram:
          return 'kg';
        case Unit.CubicMeter:
          return 'm³';
        case Unit.LongTon:
          return 'l.tn';
        case Unit.Pound:
          return 'lbs';
        case Unit.ShortTon:
          return 'tn';
        default:
          return '';
      }
    },
    moveDialog: {
      title: 'Move bulk',
    },
  },
  dashboard: {
    actions: {
      fetchMany: {
        title: 'Fetching dashboards',
        error: 'Failed to fetch dashboards',
      },
      fetch: {
        title: 'Fetching dashboard',
        error: 'Failed to fetch dashboard',
      },
      create: {
        title: 'Creating dashboard',
        error: 'Failed to create dashboard',
      },
      update: {
        title: 'Updating dashboard',
        error: 'Failed to update dashboard',
      },
      delete: {
        title: 'Deleting dashboard',
        error: 'Failed to delete dashboard',
        statusCodes: {
          409: 'Failed to delete dashboard',
        },
      },
    },
    listPage: {
      title: 'Dashboards',
      noFound: 'No dashboard found',
      loading: 'Loading dashboards...',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete dashboard ${name}`;
      },
      description: 'Are you sure you want to delete this dashboard? This action cannot be undone.',
      deleteButton: 'Yes delete dashboard',
    },
    editPage: {
      editTitle: 'Edit dashboard',
      createTitle: 'Create dashboard',
    },
    editComponentDialog: {
      editTitle: 'Edit component',
      addTitle: 'Add component',
    },
    productGroupTable: {
      title: 'Stock per product group',
    },
    productTable: {
      title: 'Stock per product',
    },
    productGroupDiagram: {
      title: 'Stock per product group',
    },
  },
  overview: {
    title: 'Overview',
  },
  releaseNotes: {
    actions: {
      fetchLatest: {
        title: 'Fetching latest release note',
        error: 'Failed to fetch latest release note',
      },
      fetchMany: {
        title: 'Fetching release notes',
        error: 'Failed to fetch release notes',
      },
    },
  },
  keybinds: {
    action: 'Action',
    keybind: 'Keybind',
  },
  reportPage: {
    selectReport: 'Select report',
    showReport: 'Show report',
    selectExportFormat: 'Select export format',
    exportReport: 'Export report',
    exportError: 'Failed to export report',
    showError: 'Failed to show report',
  },
};
